<template>
	<base-modal
		:showCloseButton="true"
		:opened="isOpen"
		:maxWidth="'2xl'"
		@close="handleDecline"
	>
		<template #body>
			<div class="p-4 space-y-6">

				<!-- Heading & Subtitle -->
				<div class="text-center space-y-3">
          <span
              class="inline-block border bg-green-50 border-none text-green-600 text-sm font-medium rounded-full px-4 py-1"
          >
						Exclusive One-time Offer
					</span>
					<h2
						id="modal-headline"
						class="text-2xl md:text-3xl font-bold leading-tight"
					>
						{{ content.heading }}
					</h2>
					<p class="text-gray-600">
						{{ content.subtitle }}
					</p>
				</div>

				<!-- Pricing Card -->
				<div
					class="bg-green-50 border-2 border-green-200 rounded-2xl p-6 space-y-4"
				>
					<!-- Price -->
					<div class="text-center">
						<div class="flex items-center justify-center gap-3">
							<span
								class="text-5xl font-bold text-gray-500 line-through"
								>$55</span
							>
							<span class="text-5xl font-bold text-gray-900"
								>$35</span
							>
						</div>
						<p class="text-gray-500 italic mt-1">
							{{ content.priceSubtitle }}
						</p>
					</div>

					<!-- Stat Boxes -->
					<div class="grid grid-cols-2 gap-3">
						<div class="bg-white rounded-lg p-3 text-center">
							<div class="text-3xl font-bold text-green-600">
								37%
							</div>
							<div class="text-gray-600 text-sm">Discount</div>
						</div>
						<div class="bg-white rounded-lg p-3 text-center">
							<div class="text-3xl font-bold text-green-600">
								{{ content.savingsAmount }}
							</div>
							<div class="text-gray-600 text-sm">
								{{ content.savingsLabel }}
							</div>
						</div>
					</div>

					<!-- Divider -->
					<hr class="border-green-200" />

					<!-- Features -->
					<div>
						<h3 class="py-3 font-bold text-gray-900 mb-3">
							{{ content.featuresHeading }}
						</h3>
						<div class="grid grid-cols-2 gap-3">
							<div
								v-for="feature in content.features"
								:key="feature"
								class="flex items-start gap-1"
							>
								<CheckCircleIcon
									class="h-5 w-5 text-green-600 shrink-0 mt-0.5"
								/>
								<span class="text-gray-700 text-sm">{{
									feature
								}}</span>
							</div>
						</div>
					</div>
				</div>

				<!-- Actions -->
				<div class="flex flex-col items-stretch gap-3">
					<button
						type="button"
						class="button rounded-md bg-yellow-400 text-black hover:bg-yellow-300 border-none px-6 py-3 text-base font-semibold w-full"
						@click="startCheckout"
					>
						Accept 37% Discount
					</button>
					<button
						type="button"
						class="text-gray-600 hover:text-gray-800 text-sm text-center no-underline hover:underline"
						@click="handleDecline"
					>
						No thanks, I want to cancel my subscription
					</button>
				</div>
			</div>
		</template>
	</base-modal>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { analytics } from "@/services/segment.ts";
import BaseModal from "./BaseModal.vue";
import { useSaveOfferModalStore } from "@/stores/saveOfferModalStore";
import type { SaveOfferVariant } from "@/stores/saveOfferModalStore";
import { CheckCircleIcon } from "@heroicons/vue/24/solid";
import { getEnv } from "@/env";

const emit = defineEmits<{
	(e: "decline"): void;
}>();

interface VariantContent {
	heading: string;
	subtitle: string;
	priceSubtitle: string;
	savingsAmount: string;
	savingsLabel: string;
	featuresHeading: string;
	features: string[];
}

const variantContentMap: Record<SaveOfferVariant, VariantContent> = {
	cost: {
		heading: "We can help with the cost so you can keep investing in your future",
		subtitle:
			"If price is the main concern, we'd like to help. Keep full access to Clover Learning for a reduced rate while you continue preparing for your next step.",
		priceSubtitle: "Per month",
		savingsAmount: "$20",
		savingsLabel: "Saved per month",
		featuresHeading: "You'll keep full access to:",
		features: [
			"10,000+ Instructional Videos",
			"5,000 exam practice questions",
			"Full exam prep banks",
			"Performance tracking and reporting",
		],
	},
	failed: {
		heading: "Don't stop now — you're close!",
		subtitle:
			"More practice can make all the difference on your second attempt – and we'd love to help. You can keep studying at a discounted rate and walk into your next attempt feeling confident.",
		priceSubtitle: "Exclusive One-time Offer",
		savingsAmount: "$20",
		savingsLabel: "Saved per month",
		featuresHeading: "Training & Exam Prep Plan Features",
		features: [
			"10,000 Instructional Videos",
			"5,000+ exam practice questions",
			"Exam prep banks",
			"Performance reporting tools",
		],
	},
};

const modalStore = useSaveOfferModalStore();
const isOpen = computed(() => modalStore.isOpen);
const content = computed(() => variantContentMap[modalStore.variant]);

const startCheckout = () => {
	analytics.track("D2C Cancel Save Offer Accepted", { variant: modalStore.variant });
	const plan = getEnv('VITE_SAVE_OFFER_PLAN', 'professional');
	const frequency = getEnv('VITE_SAVE_OFFER_FREQUENCY', '1m');
	const params = new URLSearchParams(window.location.search);
	params.set("plan", plan);
	params.set("frequency", frequency);
	params.set("save_offer", "1");
	window.location.href = `/account/checkout/offer-checkout-pay?${params.toString()}`;
};

const handleDecline = () => {
	analytics.track("D2C Cancel Save Offer Declined", { variant: modalStore.variant });
	modalStore.close();
	emit("decline");
};

watch(isOpen, (val) => {
	if (val) analytics.track("D2C Cancel Save Offer Presented", { variant: modalStore.variant });
});
</script>
